import { EntityValidationStatus, Modes } from '@constants/canvas/layers';
import { NodeDTO, NodeDTOTypes } from '@store/services/nodes/types';

export enum ValidationStatusIneligibleReason {
  NotEligibleType = 'not-eligible-type',
  EditingDisallowedInMode = 'editing-disallowed-in-mode',
}

export type NodeValidationEligibility =
  | { eligible: true }
  | { eligible: false; reason: ValidationStatusIneligibleReason };

export enum ValidationStatusNoChangeReason {
  NotEligibleType = 'not-eligible-type',
  EditingDisallowedInMode = 'editing-disallowed-in-mode',
  AlreadyHasStatus = 'already-has-status',
}

const INELIGIBLE_TO_NO_CHANGE_REASON: Record<
  ValidationStatusIneligibleReason,
  ValidationStatusNoChangeReason
> = {
  [ValidationStatusIneligibleReason.NotEligibleType]:
    ValidationStatusNoChangeReason.NotEligibleType,
  [ValidationStatusIneligibleReason.EditingDisallowedInMode]:
    ValidationStatusNoChangeReason.EditingDisallowedInMode,
};

// Mirrors backend src/projects/utils/validation-status-eligibility.ts — keep in sync.
const NOT_ELIGIBLE_TYPES = [
  NodeDTOTypes.Characteristic, // Sensitive elements
  NodeDTOTypes.EvaluatedRisk, // Risks
  NodeDTOTypes.Initiatives, // Change Initiatives / Remediation Actions
];
const DISALLOWED_IN_RISK_MANAGEMENT = [
  NodeDTOTypes.Capabilities,
  NodeDTOTypes.Functions,
];

export const NoChangeReasonLabel: Record<
  ValidationStatusNoChangeReason,
  string
> = {
  [ValidationStatusNoChangeReason.NotEligibleType]:
    'Validation status does not apply to this entity',
  [ValidationStatusNoChangeReason.EditingDisallowedInMode]:
    'Editing is not allowed in Cyber Resilience',
  [ValidationStatusNoChangeReason.AlreadyHasStatus]:
    'Already has the selected validation status',
};

export function getNodeValidationEligibility(
  node: Pick<NodeDTO, 'type'>,
  mode: Modes,
): NodeValidationEligibility {
  if (NOT_ELIGIBLE_TYPES.includes(node.type)) {
    return {
      eligible: false,
      reason: ValidationStatusIneligibleReason.NotEligibleType,
    };
  }
  if (
    mode === Modes.RiskManagement &&
    DISALLOWED_IN_RISK_MANAGEMENT.includes(node.type)
  ) {
    return {
      eligible: false,
      reason: ValidationStatusIneligibleReason.EditingDisallowedInMode,
    };
  }
  return { eligible: true };
}

export function isSetValidationStatusVisible(
  nodes: Pick<NodeDTO, 'type'>[],
  mode: Modes,
): boolean {
  return nodes.some(
    (node) => getNodeValidationEligibility(node, mode).eligible,
  );
}

export type ValidationChangeNode = Pick<
  NodeDTO,
  'id' | 'name' | 'type' | 'valid_status'
>;

export type ValidationChangePlan = {
  toChange: ValidationChangeNode[];
  unchanged: {
    node: ValidationChangeNode;
    reason: ValidationStatusNoChangeReason;
  }[];
};

/**
 * Splits nodes into the ones whose validation status will actually change when
 * the target status is applied and the ones that will stay untouched (either
 * because they are not eligible or because they already have the target status).
 */
export function getValidationChangePlan(
  nodes: ValidationChangeNode[],
  mode: Modes,
  targetStatus: EntityValidationStatus,
): ValidationChangePlan {
  const toChange: ValidationChangeNode[] = [];
  const unchanged: ValidationChangePlan['unchanged'] = [];

  nodes.forEach((node) => {
    const eligibility = getNodeValidationEligibility(node, mode);

    if (!eligibility.eligible) {
      unchanged.push({
        node,
        reason: INELIGIBLE_TO_NO_CHANGE_REASON[eligibility.reason],
      });

      return;
    }

    if (node.valid_status === targetStatus) {
      unchanged.push({
        node,
        reason: ValidationStatusNoChangeReason.AlreadyHasStatus,
      });

      return;
    }

    toChange.push(node);
  });

  return { toChange, unchanged };
}
